import { useState, useEffect } from 'react';
import { auth, googleProvider } from '../firebase';
import { 
  signInWithEmailAndPassword, 
  signInWithPopup,
  createUserWithEmailAndPassword, 
  signOut,
  sendEmailVerification,
  reload,
  getIdToken,
  onAuthStateChanged
} from 'firebase/auth';
import { validatePassword } from './passwordValidator';
import { useFirestore } from './useFirestore';
//import { useUser } from './userContext';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { loadStripe } from '@stripe/stripe-js';

export function useAuth() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const { updateData, getData } = useFirestore();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        const userData = await getData('users', firebaseUser.uid);
        setUser({ ...firebaseUser, ...userData });
      } else {
        setUser(null);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, [getData]);

  const login = async (email, password) => {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      await setUser(userCredential.user);
    } catch (error) {
      console.error('Error in login function:', error);
      if (error.code === 'auth/invalid-credential') {
        throw new Error('This email is associated with a Google account. Please sign in with Google.');
      } else {
        throw error;
      }
    }
  };

  const loginWithGoogle = async () => {
    try {
      const userCredential = await signInWithPopup(auth, googleProvider);
      const userData = await getData('users', userCredential.user.uid);
      const updatedUser = { ...userCredential.user, ...userData };
      setUser(updatedUser);
      
      if (!updatedUser.subscribed) {
        await initiateStripeWorkflow(updatedUser.email, updatedUser.uid);
      }
    } catch (error) {
      console.error('Error signing in with Google', error);
      throw error;
    }
  };

  const initiateStripeWorkflow = async (email, userId) => {
    const functions = getFunctions();
    const createCheckoutSession = httpsCallable(functions, 'createCheckoutSession');
    const { data } = await createCheckoutSession({ email, userId });
    
    const stripe = await loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);
    if (!stripe) {
      throw new Error('Failed to load Stripe');
    }
    
    const { error } = await stripe.redirectToCheckout({ sessionId: data.sessionId });
    if (error) {
      throw new Error(error.message);
    }
  };

  const signUp = async (email, password) => {
    const passwordError = validatePassword(password);
    if (passwordError) {
      throw new Error(passwordError);
    }

    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      await sendEmailVerification(userCredential.user);
      await initiateStripeWorkflow(email, userCredential.user.uid);
    } catch (error) {
      if (error.code === 'auth/email-already-in-use') {
        throw new Error('This email is already in use. Please try logging in or use a different email.');
      } else {
        console.error('Error signing up:', error);
        throw new Error('An error occurred during sign up. Please try again.');
      }
    }
  };

  const logout = async () => {
    try {
      await signOut(auth);
      setUser(null); // Clear user data
    } catch (error) {
      console.error('Error signing out:', error);
      throw error;
    }
  };

  const updateUserSubscription = async (userId) => {
    try {
      await updateData('users', userId, { subscribed: true });
    } catch (error) {
      console.error('Error updating user subscription:', error);
      throw error;
    }
  };

  const isEmailVerified = () => auth.currentUser?.emailVerified ?? false;

  const sendVerificationEmail = async () => {
    if (auth.currentUser && !auth.currentUser.emailVerified) {
      try {
        await sendEmailVerification(auth.currentUser);
        console.log('Verification email sent');
        // Optionally, you can update the user state here if needed
      } catch (error) {
        console.error('Error sending verification email:', error);
        throw error;
      }
    } else {
      console.log('User is already verified or not logged in');
    }
  };

  const refreshUser = async (force = false) => {
    if (auth.currentUser) {
      try {
        if (force) {
          // Only force token refresh when absolutely necessary
          await getIdToken(auth.currentUser, true);
        }
        await reload(auth.currentUser);
        const freshUserData = await getData('users', auth.currentUser.uid);
        setUser({ ...auth.currentUser, ...freshUserData });
      } catch (error) {
        console.error('Error refreshing user data:', error);
        if (error.code === 'auth/network-request-failed') {
          console.log('Network error. Retrying in 5 seconds...');
          setTimeout(() => refreshUser(force), 5000);
        } else if (error.code === 'auth/user-token-expired') {
          console.log('User token expired. Logging out...');
          await logout();
        } else if (error.code === 'auth/quota-exceeded') {
          console.log('Quota exceeded. Skipping refresh.');
          // Optionally, you could implement a backoff strategy here
        }
      }
    }
  };

  return { 
    user, 
    loading, 
    login, 
    loginWithGoogle, 
    signUp, 
    logout, 
    updateUserSubscription, 
    isEmailVerified,
    sendVerificationEmail,
    refreshUser
  };
}

export async function isAuthenticated() {
  return auth.currentUser !== null;
}